/**
 * Feed surface styles (B-WP11b). Injected once per document (mirror of
 * features/groups/styles.ts injectGroupStyles). Reuses the group page shell
 * tokens — every custom property here is one the group styles already rely on
 * (--text, --line, --faint, --bg, --space-*, --radius, --font-mono) so no
 * undefined-var transparent-render trap (incident 2026-07-09). Quiet, hairline,
 * sans prose with mono for quantities — the rating readout and the cook/shared
 * timestamp render in --font-mono + tabular-nums, matching the group ReportRow /
 * GroupShareCard convention (design.md §4 — mono for data). Rule 7: ≥44px targets.
 */

const FEED_STYLE_ID = "proof-feed-css";

const CSS = `
  .proof-feed { max-width: 640px; margin: 0 auto; padding: var(--space-4, 24px) var(--space-3, 16px) 96px; }
  .proof-feed__back {
    min-height: 44px; display: inline-flex; align-items: center;
    background: transparent; border: none; padding: 0;
    color: var(--faint, #8a8578); font-size: 14px; cursor: pointer;
  }
  .proof-feed__back:hover { color: var(--text, #1a1814); }
  .proof-feed__header { margin-bottom: var(--space-3, 16px); }
  .proof-feed__title { font-size: 20px; font-weight: 600; color: var(--text, #1a1814); margin: 0; }
  .proof-feed__chips { display: flex; gap: var(--space-2, 8px); margin-bottom: var(--space-4, 24px); }
  .proof-feed__chip {
    min-height: 44px; padding: 0 var(--space-3, 16px);
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid var(--line, #e0ddd6); border-radius: 999px;
    background: transparent; color: var(--faint, #8a8578);
    font-size: 14px; font-weight: 500; cursor: pointer;
  }
  .proof-feed__chip[aria-pressed="true"] {
    color: var(--text, #1a1814);
    border-color: color-mix(in srgb, var(--text, #1a1814) 26%, var(--line, #e0ddd6));
    background: color-mix(in srgb, var(--text, #1a1814) 5%, transparent);
  }
  .proof-feed__list { display: flex; flex-direction: column; gap: var(--space-3, 16px); }
  .proof-feed-card {
    border: 1px solid var(--line, #e0ddd6); border-radius: var(--radius, 10px);
    padding: var(--space-3, 16px); background: var(--bg, #faf8f3);
    display: flex; flex-direction: column; gap: 6px;
  }
  .proof-feed-card__head { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
  .proof-feed-card__handle { font-size: 14px; font-weight: 600; color: var(--text, #1a1814); }
  .proof-feed-card__verb { font-size: 14px; color: var(--faint, #8a8578); }
  .proof-feed-card__title { font-size: 15px; font-weight: 500; color: var(--text, #1a1814); }
  a.proof-feed-card__title { text-decoration: none; }
  a.proof-feed-card__title:hover { text-decoration: underline; }
  /* The rating figure is the shared <Rating> readout (design.md §6.8) and
     carries its own mono / tabular-nums / accent-star styling. */
  .proof-feed-card__rating { display: flex; }
  .proof-feed-card__excerpt { font-size: 14px; color: var(--text, #1a1814); line-height: 1.6; }
  .proof-feed-card__message { font-size: 14px; color: var(--text, #1a1814); line-height: 1.6; }
  .proof-feed-card__time {
    font-family: var(--font-mono);
    font-size: 12px; color: var(--faint, #8a8578);
    font-variant-numeric: tabular-nums; font-feature-settings: "tnum";
  }
  .proof-feed-card__nod {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
    margin-top: 4px;
  }
  .proof-feed-card__nod-btn {
    min-height: 44px; padding: 0 var(--space-3, 16px);
    display: inline-flex; align-items: center;
    border: 1px solid var(--line, #e0ddd6); border-radius: 999px;
    background: transparent; color: var(--faint, #8a8578);
    font-size: 13px; font-weight: 500; cursor: pointer;
  }
  .proof-feed-card__nod-btn[aria-pressed="true"] {
    color: var(--text, #1a1814);
    border-color: color-mix(in srgb, var(--text, #1a1814) 26%, var(--line, #e0ddd6));
    background: color-mix(in srgb, var(--text, #1a1814) 5%, transparent);
  }
  .proof-feed-card__nod-btn:disabled { cursor: default; opacity: 0.6; }
  .proof-feed-card__nod-line { font-size: 13px; color: var(--faint, #8a8578); }
  .proof-feed-card__nod-error { font-size: 13px; color: var(--del, #c03a24); }
  .proof-feed__more {
    min-height: 44px; margin: var(--space-4, 24px) auto 0; display: block;
    padding: 0 var(--space-4, 24px);
    border: 1px solid var(--line, #e0ddd6); border-radius: var(--radius, 10px);
    background: transparent; color: var(--text, #1a1814); font-size: 14px; cursor: pointer;
  }
  .proof-feed__more:disabled { color: var(--faint, #8a8578); cursor: default; }
  .proof-feed__empty { text-align: center; color: var(--faint, #8a8578); padding: 64px 16px; font-size: 15px; line-height: 1.5; }
  .proof-feed__status { text-align: center; color: var(--faint, #8a8578); padding: 40px 16px; font-size: 14px; }
`;

export function injectFeedStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(FEED_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = FEED_STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}
